/* eslint-disable max-lines */
import {
  Alarm,
  AlarmTypeColorMapping,
  AlarmStatus,
  User,
} from '@hakimo-ui/hakimo/types';
import { Row, TableData } from '@hakimo-ui/hakimo/ui-table';
import { Label } from '@hakimo-ui/shared/ui-base';
import React from 'react';
import { getTapColor } from '@hakimo-ui/hakimo/feature-shared';
import { mapStatusToLabelType } from '../shared/util/common';
import {
  getAlarmTypeColorClass,
  hasAlarmTypeColor,
  setColorMappings,
} from '../shared/util/alarmTypeColors';
import { AlarmColumn, FilterValues } from './types';
/*
Order of columns here is what is displayed in the table
*/
export const columns: AlarmColumn[] = [
  {
    id: 'timestamp',
    name: 'Time',
  },
  {
    id: 'status',
    name: 'Status',
    showAtBreakpoint: 'xl',
  },
  {
    id: 'tap',
    name: 'TAP',
    showAtBreakpoint: 'xl',
  },
  {
    id: 'type',
    name: 'Type',
    showAtBreakpoint: 'md',
  },
  {
    id: 'sourceSystem',
    name: 'Source System',
    showAtBreakpoint: 'lg',
  },
  {
    id: 'location',
    name: 'Location',
    showAtBreakpoint: 'lg',
  },
  {
    id: 'sourceEntity',
    name: 'Source Entity',
    showAtBreakpoint: 'md',
  },
  { id: 'employee', name: 'Employee', showAtBreakpoint: 'lg' },
  { id: 'tenant', name: 'Tenant', showAtBreakpoint: 'lg' },
];

const columnConstructor = {
  timestamp: (item: Alarm) => ({
    value: new Date(item.timestamp).toLocaleString(),
  }),
  status: (item: Alarm) => ({
    value: (
      <Label text={item.status} type={mapStatusToLabelType(item.status)} />
    ),
  }),
  tap: (item: Alarm) => {
    const tapColor = item.tap ? getTapColor(item.tap) : null;
    return {
      value: tapColor ? (
        <div>
          <span
            className="inline-block h-4 w-4 rounded-full align-middle"
            style={{ backgroundColor: tapColor }}
          />
          <span className={`px-2 py-1 text-xs font-medium`}>{item.tap}</span>
        </div>
      ) : (
        '-'
      ),
    };
  },
  type: (item: Alarm) => {
    const shouldShowColorCoding =
      item.tenant.config?.alarmTypeColorCoding === true;
    if (shouldShowColorCoding && hasAlarmTypeColor(item.type)) {
      const colorClass = getAlarmTypeColorClass(item.type);
      return {
        value: (
          <div className="flex items-center gap-1">
            <div className={`h-4 w-1 rounded ${colorClass}`} />
            <span>{item.type}</span>
          </div>
        ),
      };
    } else {
      return {
        value: <span>{item.type}</span>,
      };
    }
  },
  sourceSystem: (item: Alarm) => ({ value: item.sourceSystem }),
  location: (item: Alarm) => ({
    value: item.location?.name ?? 'Unknown Location',
  }),
  sourceEntity: (item: Alarm) => ({
    value: item.sourceEntity?.name ? item.sourceEntity?.name : 'Unknown',
  }),
  employee: (item: Alarm) => ({ value: item.employee?.name }),
  tenant: (item: Alarm) => ({ value: item.tenant.name }),
};

export function getTableData(
  items: Alarm[],
  onClickRowCb: (
    item: Alarm,
    event: React.MouseEvent | React.KeyboardEvent
  ) => void,
  canViewTenantColumn: boolean,
  shownColumns: string[],
  colorMappings: AlarmTypeColorMapping[] = []
): TableData {
  setColorMappings(colorMappings);
  const displayedColumns = columns
    .filter((column) => shownColumns.includes(column.id))
    .filter((col) => {
      return col.id !== 'tenant' || canViewTenantColumn;
    });

  const rows: Row[] = items.map<Row>((item) => {
    return {
      onClick: (event: React.MouseEvent | React.KeyboardEvent) =>
        onClickRowCb(item, event),
      id: item.id,
      cells: displayedColumns.map((col) => {
        const index = col.id;
        return columnConstructor[index](item);
      }),
    };
  });

  return {
    columns: displayedColumns,
    rows,
  };
}

export const applyAutoFilters = (
  filterValues: FilterValues,
  user: User | null | undefined,
  hasManualOverride: boolean
): FilterValues => {
  let updatedValues = { ...filterValues };

  if (user?.alarm_types && user.alarm_types.length > 0 && !hasManualOverride) {
    updatedValues = {
      ...updatedValues,
      type: {
        values: user.alarm_types.map((at) => ({
          id: at.id,
          name: at.type,
        })),
        negative: false,
      },
    };
  }

  if (
    user?.default_alarm_status_list &&
    user.default_alarm_status_list.length > 0 &&
    !hasManualOverride
  ) {
    updatedValues = {
      ...updatedValues,
      status: {
        values: user.default_alarm_status_list as AlarmStatus[],
        negative: false,
      },
    };
  }

  return updatedValues;
};

export const isAutoAppliedFilter = (
  filterValues: FilterValues,
  user: User | null | undefined
): boolean => {
  const userAlarmTypes = user?.alarm_types || [];
  const userDefaultStatuses = user?.default_alarm_status_list || [];

  const isAlarmTypeAutoApplied =
    userAlarmTypes.length > 0 &&
    filterValues.type?.values.length === userAlarmTypes.length &&
    filterValues.type.values.every((filterValue) =>
      userAlarmTypes.some(
        (userType) =>
          userType.id === filterValue.id && userType.type === filterValue.name
      )
    ) &&
    !filterValues.type.negative;

  const isStatusAutoApplied =
    userDefaultStatuses.length > 0 &&
    filterValues.status?.values.length === userDefaultStatuses.length &&
    filterValues.status.values.every((filterValue) =>
      userDefaultStatuses.includes(filterValue)
    ) &&
    !filterValues.status.negative;

  return isAlarmTypeAutoApplied || isStatusAutoApplied;
};
